import { brand } from '@alison/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useState } from 'react';
import closeIcon from '../../assets/ai-insights/close-x.svg';
import downloadIcon from '../../assets/ai-insights/download.svg';
import thinkingRings from '../../assets/ai-insights/thinking-rings.svg';
import { AskAlisonThinkingIcon } from './AskAlisonThinkingIcon.js';
import { FieldTooltip } from './FieldHeader.js';
import { ReportView } from './ReportView.js';
import { downloadReport } from './reportDownload.js';
import { parseReport } from './reportResult.js';
import { summaryOf } from './resultRows.js';
import { failureReason } from './runFailure.js';
import type { WorkflowRun } from './workflows.types.js';

const STATUS_SWAP_MS = 2_000;
const MARK_SIZE = 64;
const FRAME_STROKE_ALPHA = 0.55;
const HEADER_BUTTON_SX = {
  gap: 1,
  px: 2,
  py: 1,
  minWidth: 0,
  borderRadius: 0.9,
  border: '1px solid',
  borderColor: 'text.muted',
  color: 'text.muted',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 'normal',
  '& .MuiButton-startIcon': { m: 0 },
};

const sonar = keyframes`
  from { transform: scale(1); opacity: 0.4; }
  to { transform: scale(1.6); opacity: 0; }
`;

const riseIn = keyframes`
  from { transform: translateY(6px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

/** `labels` are the AI Insights ActionDisplay.labels: every word the panel shows. */
export interface AiInsightsPanelProps {
  labels: Record<string, string>;
  tooltip: string | null;
  runningSteps: string[];
  run: WorkflowRun | null;
  pending: boolean;
  error: string | null;
  onRetry: () => void;
  onClose: () => void;
}

function ThinkingMark(): ReactNode {
  return (
    <Box sx={{ position: 'relative', width: MARK_SIZE, height: MARK_SIZE, flexShrink: 0 }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '1.5px solid',
          borderColor: brand.violet,
          animation: `${sonar} 1.6s ease-out infinite`,
        }}
      />
      <img src={thinkingRings} alt="" style={{ position: 'relative', display: 'block' }} />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AskAlisonThinkingIcon />
      </Box>
    </Box>
  );
}

function Thinking({ lines }: { lines: string[] }): ReactNode {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (lines.length <= 1) return;
    const interval = setInterval(
      () => setIndex((current) => (current + 1) % lines.length),
      STATUS_SWAP_MS,
    );
    return () => clearInterval(interval);
  }, [lines.length]);

  return (
    <Stack direction="row" sx={{ gap: 3, alignItems: 'center' }}>
      <ThinkingMark />
      <Typography role="status" aria-live="polite" sx={{ fontSize: 16, fontWeight: 600 }}>
        <Box
          component="span"
          key={index}
          sx={{ display: 'inline-block', animation: `${riseIn} 300ms ease-out` }}
        >
          {lines[index]}
        </Box>
      </Typography>
    </Stack>
  );
}

export function AiInsightsPanel({
  labels,
  tooltip,
  runningSteps,
  run,
  pending,
  error,
  onRetry,
  onClose,
}: AiInsightsPanelProps): ReactNode {
  const failed = !pending && (error !== null || run?.status === 'failed');
  const completed = !pending && !failed && run?.status === 'completed' ? run : null;
  const report = completed === null ? null : parseReport(completed.result);
  const title = labels['panel_title'] ?? '';

  return (
    <Stack
      data-testid="ai-insights-panel"
      sx={{
        gap: 4,
        px: 4,
        pt: 3.2,
        pb: 4,
        borderRadius: 1.2,
        border: '1.5px solid',
        borderColor: alpha(brand.violet, FRAME_STROKE_ALPHA),
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{ fontSize: 11, fontWeight: 600, color: brand.violet, textTransform: 'uppercase' }}
        >
          {title}
        </Typography>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
          {report === null ? null : (
            <Button
              sx={HEADER_BUTTON_SX}
              startIcon={<img src={downloadIcon} alt="" />}
              onClick={() => downloadReport(labels, report)}
            >
              {labels['download']}
            </Button>
          )}
          <IconButton aria-label="Close" size="small" sx={{ p: 1, m: -1 }} onClick={onClose}>
            <img src={closeIcon} alt="" />
          </IconButton>
        </Stack>
      </Stack>
      {failed ? (
        <Stack sx={{ gap: 2, alignItems: 'flex-start' }}>
          <Alert severity="error" sx={{ alignSelf: 'stretch' }}>
            {error ?? failureReason(run?.errorMessage ?? null) ?? 'This run failed.'}
          </Alert>
          <Button variant="outlined" size="small" onClick={onRetry}>
            {labels['retry']}
          </Button>
        </Stack>
      ) : completed === null ? (
        <Thinking lines={runningSteps.length === 0 ? [title] : runningSteps} />
      ) : (
        <>
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: 22, fontWeight: 500 }}>
              {labels['overview']}
            </Typography>
            <FieldTooltip tooltip={tooltip} />
          </Stack>
          {report === null ? (
            <Typography sx={{ fontSize: 14 }}>{summaryOf(completed.result)}</Typography>
          ) : (
            <ReportView result={report} labels={labels} />
          )}
        </>
      )}
    </Stack>
  );
}
